// ─── Angular Imports ───────────────────────────────────────────────────────────
import { Component, DestroyRef, inject, ViewChild, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';

// ─── Angular Material Imports ─────────────────────────────────────────────────
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

// ─── Third-Party Imports ───────────────────────────────────────────────────────
import { finalize, Subject, takeUntil } from 'rxjs';

// ─── Application Imports ───────────────────────────────────────────────────────
import { BatchesService } from 'src/app/services/batches.services';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { TagsService } from 'src/app/services/tags.service';
import { SignalrService } from 'src/app/services/signalr.service';
import { SignalrGroup } from 'src/app/services/signalr-group';
import { SignalRStaticGroups } from 'src/app/constants/signalr.constant';
import { ProductsService } from 'src/app/services/products.service';
import { AuthService } from 'src/app/services/auth.service';
import { TableSortService } from 'src/app/services/table-sort.service';
import { Permission } from 'src/app/constants/permission.constant';
import { TableId } from 'src/app/constants/table.constant';

import { IBatch, EStateEntity } from 'src/app/models/batches/batch';
import { BatchListResult } from 'src/app/models/batches/batch-result';
import { OrganizationResult } from 'src/app/models/organizations/organization-result';
import { IPreference, IUserProfile } from 'src/app/models/users/userProfile';
import { Tag } from 'src/app/models/tag';

import { BatchCreateComponent } from '../batch-create/batch-create.component';

import { IMAGERY_VIEW, POST_VIEW, CLIENT_VIEW, ViewConfig } from './batch-list.view';
import { BatchUpdateComponent } from '../batch-update/batch-update.component';
import { DeleteModalComponent } from 'src/app/components/shared/delete-modal/delete-modal.component';
import { TagsComponent } from 'src/app/components/shared/tags/tags.component';

@Component({
  selector: 'app-list-batches',
  templateUrl: './batch-list.component.html',
  styleUrls: ['./batch-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class BatchListComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // ─── State ───────────────────────────────────────────────────────────────────
  Permission = Permission;
  updating: boolean = false;

  get canViewOrganizations(): boolean {
    return this.authService.hasPermission(Permission.OrganizationsRead);
  }

  get viewConfig(): ViewConfig {
    if (this.canViewOrganizations) {
      if (this.authService.hasPermission(Permission.ProductsWorkflowFilter)) {
        return IMAGERY_VIEW;
      }
      if (this.authService.hasPermission(Permission.ShootAssetsRead)) {
        return POST_VIEW;
      }
    }
    return CLIENT_VIEW;
  }

  displayedColumns: string[] = [
    'favorite',
    'jobNumber',
    'name',
    'description',
    'createdAt',
    'Actions',
  ];
  dataSource = new MatTableDataSource<IBatch>();
  batches: IBatch[] = [];
  batchTags: Tag[] = [];
  filteredBatchtags: Tag[] = [];
  listBatchFavorite: (string | null)[] = [];
  isFavorite: boolean = false;
  isSharedFilter: boolean = false;
  serverErrorMessage: string = '';
  currentConfigDate!: IPreference;
  user!: IUserProfile | null;

  // ─── Form Controls ───────────────────────────────────────────────────────────
  includeToggleControl = new FormControl<boolean>(false);
  selectedFiltersControl = new FormControl<string[]>([]);

  // ─── Observables ─────────────────────────────────────────────────────────────
  private readonly destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  // ─── Constructor ─────────────────────────────────────────────────────────────
  constructor(
    private router: Router,
    private batchesService: BatchesService,
    private productsService: ProductsService,
    private organizationsService: OrganizationsService,
    private tagsService: TagsService,
    private signalrService: SignalrService,
    public authService: AuthService,
    private tableSortService: TableSortService,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
  ) {}

  // ─── Lifecycle Hooks ────────────────────────────────────────────────────────
  ngOnInit() {
    this.initializeColumns();
    this.loadInitialData();
    this.initSignalR();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.signalrService.clearBatches();
  }

  // ─── Initialization Methods ─────────────────────────────────────────────────
  private initializeColumns() {
    this.displayedColumns = Object.keys(this.viewConfig);
  }

  private loadInitialData() {
    this.getListBatches();
    this.getListBatchFavorite();
    this.authService.currentPreference.subscribe((res) => {
      this.currentConfigDate = res;
    });
    this.authService
      .getUserInformation()
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile: IUserProfile) => {
        this.user = profile;
        this.includeToggleControl.setValue(profile.includeFilteredBatches);
        profile.filteredBatchTagIds.forEach((tagId) => {
          this.selectedFiltersControl.value?.push(tagId);
        });
      });
  }

  // ─── SignalR Methods ────────────────────────────────────────────────────────
  private initSignalR() {
    if (!this.authService.hasPermission(Permission.BatchesRead)) return;
    SignalrGroup.join({
      signalrService: this.signalrService,
      destroyRef: this.destroyRef,
      group: SignalRStaticGroups.BatchesList,
      data$: this.signalrService.batchesData,
      onData: (data: any) => {
        if (data.id) {
          const index = this.batches.findIndex((p) => p.id === data.id);
          if (index >= 0) {
            this.batches[index] = data;
          } else {
            this.batches.push(data);
          }
          this.dataSource.data = this.batches;
        }
      },
    });
  }

  // ─── Data Fetching Methods ──────────────────────────────────────────────────
  getListBatches() {
    this.updating = true;
    this.batchesService
      .listBatches()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.updating = false)),
      )
      .subscribe((result: BatchListResult) => {
        this.batches = result?.batches || [];
        this.parseBatchData();
      });
    this.getBatchTags();
  }

  private getBatchTags() {
    this.tagsService
      .listTags('batch')
      .pipe(takeUntil(this.destroy$))
      .subscribe((tags: Tag[]) => {
        this.batchTags = tags || [];
        this.filteredBatchtags = tags || [];
      });
  }

  getListBatchFavorite() {
    this.batchesService
      .listBatches(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: BatchListResult) => {
        this.listBatchFavorite = (result?.batches || []).map((batch) => batch.id);
      });
  }

  // ─── Data Parsing Methods ───────────────────────────────────────────────────
  private parseBatchData() {
    if (!this.canViewOrganizations) {
      this.batches.forEach((batch) => {
        if (batch.productsApprovedCount === batch.productCount && batch.productCount !== 0) {
          batch.status = EStateEntity.COMPLETE;
        } else if (batch.productsApprovedCount > 0) {
          batch.status = EStateEntity.IN_Progress;
        } else {
          batch.status = EStateEntity.PENDING;
        }
      });
    }
    const filteredBatches = this.batches
      .filter((el) => (this.isFavorite ? this.listBatchFavorite.includes(el.id) : true))
      .filter((el) => (this.isSharedFilter ? el.isSharedWithSkyTeam === true : true));
    this.dataSource = new MatTableDataSource<IBatch>(filteredBatches);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.tableSortService.applySortToMatSort(this.sort, TableId.BATCH_LIST);
  }

  onSortChange(sort: Sort): void {
    this.tableSortService.setSortState(TableId.BATCH_LIST, sort);
  }

  // ─── Action Methods ─────────────────────────────────────────────────────────
  manageBatch(batch: IBatch) {
    this.batchesService.setCurrentBatch(batch);
    if (this.canViewOrganizations) {
      this.organizationsService.getOrganization(batch.organizationId!).subscribe((result: OrganizationResult) => {
        this.organizationsService.setCurrentOrg(result.organization);
      });
    }
    this.productsService.resetLatestProductLists();
    this.router.navigate([`batches/${batch.id}/products`]);
  }

  detailBatch(batch: IBatch) {
    this.batchesService.setCurrentBatch(batch);
    this.router.navigateByUrl(`batches/${batch.id}`);
  }

  createBatch() {
    this.dialog.open(BatchCreateComponent).afterClosed().subscribe();
  }

  updateBatch(batch: IBatch) {
    this.dialog.open(BatchUpdateComponent, { data: batch }).afterClosed().subscribe();
  }

  deleteBatch(batch: IBatch) {
    this.dialog
      .open(DeleteModalComponent, {
        data: {
          header: `Delete Batch: ${batch.name}`,
          message: 'Are you sure you want to delete this batch and all subsequent products?',
        },
      })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.doDeleteBatch(batch);
        }
      });
  }

  private doDeleteBatch(batch: IBatch) {
    this.updating = true;
    this.batchesService
      .deleteBatch(batch)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.updating = false)),
      )
      .subscribe(() => {
        this.snackBar.open('Successfully Deleted', 'close', {
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          duration: 2000,
          panelClass: ['success'],
        });
        this.getListBatches();
      });
  }

  filterBatchFavorite() {
    this.isFavorite = !this.isFavorite;
    this.parseBatchData();
  }

  filterBatchShared() {
    this.isSharedFilter = !this.isSharedFilter;
    this.parseBatchData();
  }

  applyFilteredBatches() {
    this.batchesService
      .updateUsersBatchTags(
        this.selectedFiltersControl.value!,
        this.includeToggleControl.value!,
        this.user?.id!,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.user!.includeFilteredBatches = this.includeToggleControl.value!;
        this.user!.filteredBatchTagIds = this.selectedFiltersControl.value!;
        this.getListBatches();
      });
  }

  addBatchTags(batch: IBatch) {
    this.dialog
      .open(TagsComponent, {
        data: {
          entity: batch,
          entityType: 'batch',
          tags: this.batchTags,
        },
      })
      .afterClosed()
      .subscribe(() => {
        this.getBatchTags();
      });
  }

  setPageSizePreference(event: any) {
    if (event.pageSize !== this.currentConfigDate.pageSize) {
      this.currentConfigDate.pageSize = event.pageSize;
      const payload = {
        name: this.user!.name,
        preference: this.currentConfigDate,
      };
      this.authService.updatePreference(payload).subscribe();
    }
  }

  public applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toUpperCase();
  }

  public applyFilterBatchTags(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.trim();
    this.filteredBatchtags = this.batchTags.filter((tag) =>
      tag.tag.toLowerCase().includes(filterValue),
    );
  }
}
